import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, TextField, ButtonBase, CircularProgress, Chip } from '@mui/material';
import { Search, TableRestaurant } from '../ui/Icons';
import { formatPrice } from '../../utils/formatPrice';

// One item tile. Memoized so that cart/tab changes in the parent POS page don't
// re-render every tile — only tiles whose item/currency/onPick actually change.
// Requires a stable onPick (the parent passes a useCallback'd handler).
const MenuGridCell = React.memo(({ item, currency, onPick }) => (
  <ButtonBase
    onClick={() => onPick(item)}
    focusRipple
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      textAlign: 'start',
      p: 1.5,
      height: 96,
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      backgroundColor: 'background.paper',
      transition: 'border-color 150ms, box-shadow 150ms',
      '&:hover': { borderColor: 'primary.main', boxShadow: '0 2px 10px rgba(15,91,69,0.12)' },
    }}
  >
    <Typography sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
      {item.name}
    </Typography>
    <Typography sx={{ fontWeight: 700, color: 'primary.main' }}>
      {formatPrice(item.price, currency)}
    </Typography>
  </ButtonBase>
));

/**
 * Fast item picker for the POS. Tapping an item calls onPick(item); the parent
 * decides whether to open the addon dialog or add it straight to the cart.
 * Disabled until a tab is active so staff can't build an order with nowhere to put it.
 */
const MenuGrid = ({ items = [], currency, onPick, disabled, loading }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('__all__');

  const available = useMemo(
    () => items.filter((it) => it.is_available !== 0 && it.is_available !== false),
    [items]
  );

  // Distinct categories, in the order items arrive (the API sorts by section).
  // A "category" maps to the item's `section` field — same convention as AdminMenuV2.
  const categories = useMemo(() => {
    const seen = [];
    for (const it of available) {
      const key = it.section || 'Other';
      if (!seen.includes(key)) seen.push(key);
    }
    return seen;
  }, [available]);

  // If the menu changes (e.g. switching restaurant) and the chosen category is
  // gone, fall back to All so the grid never silently shows nothing.
  useEffect(() => {
    if (activeCategory !== '__all__' && !categories.includes(activeCategory)) {
      setActiveCategory('__all__');
    }
  }, [categories, activeCategory]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return available.filter((it) => {
      if (activeCategory !== '__all__' && (it.section || 'Other') !== activeCategory) return false;
      if (q && !(it.name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [available, query, activeCategory]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ position: 'relative', mb: 1 }}>
        <Box sx={{ position: 'absolute', insetInlineStart: 12, top: '50%', transform: 'translateY(-50%)', color: 'text.secondary', pointerEvents: 'none', display: 'flex' }}>
          <Search size={18} />
        </Box>
        <TextField
          size="small"
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('pos.menu.search', 'Search items')}
          disabled={disabled}
          inputProps={{ 'aria-label': t('pos.menu.search', 'Search items') }}
          sx={{ '& .MuiInputBase-input': { paddingInlineStart: '36px' } }}
        />
      </Box>

      {!loading && !disabled && categories.length > 1 && (
        <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.75, overflowX: 'auto', mb: 1, pb: 0.5 }}>
          <Chip
            label={t('pos.menu.allCategories', 'All')}
            size="small"
            color={activeCategory === '__all__' ? 'primary' : 'default'}
            variant={activeCategory === '__all__' ? 'filled' : 'outlined'}
            onClick={() => setActiveCategory('__all__')}
            sx={{ flexShrink: 0 }}
          />
          {categories.map((cat) => (
            <Chip
              key={cat}
              label={cat}
              size="small"
              color={activeCategory === cat ? 'primary' : 'default'}
              variant={activeCategory === cat ? 'filled' : 'outlined'}
              onClick={() => setActiveCategory(cat)}
              sx={{ flexShrink: 0 }}
            />
          ))}
        </Box>
      )}

      {!loading && !disabled && filtered.length > 0 && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
          {t('pos.menu.itemCount', '{{count}} items', { count: filtered.length })}
        </Typography>
      )}

      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : disabled ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 160, color: 'text.secondary', textAlign: 'center', px: 2 }}>
            <TableRestaurant size={32} />
            <Typography sx={{ mt: 1.5, fontSize: 14 }}>
              {t('pos.menu.disabledHint', 'Pick or open a table to start an order.')}
            </Typography>
          </Box>
        ) : filtered.length === 0 ? (
          <Typography sx={{ textAlign: 'center', color: 'text.secondary', py: 6 }}>
            {query.trim()
              ? t('pos.menu.noMatch', 'No items match your search.')
              : t('pos.menu.empty', 'No items found.')}
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
              gap: 1.5,
            }}
          >
            {filtered.map((item) => (
              <MenuGridCell key={item.id} item={item} currency={currency} onPick={onPick} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Memoized so the grid skips re-rendering when the parent re-renders for
// unrelated reasons (cart edits, tab selection). items/currency/onPick are stable
// across those renders, so the grid only re-renders when the menu actually changes.
export default React.memo(MenuGrid);
